/*
**  rui-inline-notification.js
**
**  rui-inline-notification component
*/
import { LitElement, html, css } from 'lit-element';
import { classMap } from 'lit-html/directives/class-map';
import { ruiIsDataDense } from '../common/rui-common-dom-detection.js';
export class RuiInlineNotification extends LitElement {
    static get is() {
        return 'rui-inline-notification';
    }
    static get properties() {
        return {
            type: { type: String },
            iconType: { type: String },
            isClosable: { type: Object },
            isNotificationDisplayed: { type: Object }
        };
    }
    constructor() {
        super();
        this.type = 'information';
        this.iconType = '';
        this.isClosable = false;
        this.isNotificationDisplayed = true;
    }
    hideNotification(e) {
        e.preventDefault();
        e.stopPropagation();
        this.isNotificationDisplayed = false;
    }
    render() {
        let statusIconType = this.type.toLowerCase();
        let notificationClassName = 'rui-inline-notification-info';
        let iconContext = '';
        if (statusIconType === 'error') {
            notificationClassName = 'rui-inline-notification-error';
            iconContext = 'status-icon-error';
        }
        else if (statusIconType === 'warning') {
            notificationClassName = 'rui-inline-notification-warning';
            iconContext = 'status-icon-warning';
        }
        else if (statusIconType === 'success') {
            notificationClassName = 'rui-inline-notification-success';
            iconContext = 'status-icon-success';
        }
        else {
            notificationClassName = 'rui-inline-notification-info';
            iconContext = 'status-icon-information';
        }
        const classes = {
            ['rui-inline-notification']: true,
            ['rui-inline-notification-dense']: ruiIsDataDense(),
            [notificationClassName]: true,
            ['rui-inline-notification-hidden']: !this.isNotificationDisplayed
        };
        return html `
            <div class="${classMap(classes)}">
                <div class="rui-inline-notification-container">
                    ${this.iconType ? html `<rui-icon iconType="${this.iconType}" context="${iconContext}" size="18px" class="rui-inline-notification-icon" aria-hidden="true"></rui-icon>` : ''}
                    <div class="rui-inline-notification-content">
                        <slot></slot>
                    </div>
                    ${this.isClosable ? html `<button type="button" class="close" aria-label="Close" @click="${this.hideNotification}"><span aria-hidden="true">&times;</span></button>` : ''}
                </div>
            </div>
        `;
    }
    static get styles() {
        return css `
            :host {
                display: block;
            }

            .rui-inline-notification {
                border-radius: 2px;
                border-width: 1px 1px 1px 4px;
                border-style: solid;
                margin: 0 0 15px 0;
                padding: 15px;
            }
            
            .rui-inline-notification-dense {
                padding: 10px;
            }
            
            .rui-inline-notification-info {
                background-color: var(--notification-info-bg, #eaf7ff);
                border-color: var(--notification-info-border, #eaf7ff);
                border-left-color: var(--notification-info-accent, #56ace1);
                color: var(--notification-info-fg, #666);
            }
            
            .rui-inline-notification-error {
                background-color: var(--notification-error-bg, #fde1df);
                border-color: var(--notification-error-border, #fde1df);
                border-left-color: var(--notification-error-accent, #f7624a);
                color: var(--notification-error-fg, #666);
            }
            
            .rui-inline-notification-warning {
                background-color: var(--notification-warning-bg, #fff8ea);
                border-color: var(--notification-warning-border, #fff8ea);
                border-left-color: var(--notification-warning-accent, #efb71f);
                color: var(--notification-warning-fg, #666);
            }
            
            .rui-inline-notification-success {
                background-color: var(--notification-success-bg, #e0f1e0);
                border-color: var( --notification-success-border, #e0f1e0);
                border-left-color: var(--notification-success-accent, #6abf6c);
                color: var(--notification-success-fg, #666);
            }
            
            .rui-inline-notification-container {
                display: flex;
                align-items: flex-start;
            }
            
            .rui-inline-notification-icon {
                flex: 0 0 auto;
                margin: 1px 10px -4px 0px;
            }
            
            .rui-inline-notification-content {
                flex: 1 0 0%;
            }
            
            button.close {
                background: none;
                border: none;
                box-sizing: border-box;
                cursor: pointer;
                font-family: var(--font-family, "Segoe UI", "Open Sans", "Helvetica Neue", Arial, sans-serif);
                font-size: 21px;
                font-weight: 700;
                line-height: 1;
                margin: 0 0 0 5px;
                max-height: 20px;
                padding: 0 3px;
                opacity: 1;
                text-shadow: none;
            }
            
            .rui-inline-notification-success button.close:hover,
            .rui-inline-notification-info button.close:hover,
            .rui-inline-notification-warning button.close:hover,
            .rui-inline-notification-error button.close:hover {
                color: var(--text-regular, #181830);
            }
            
            .rui-inline-notification-success button.close:focus,
            .rui-inline-notification-success button.close:active,
            .rui-inline-notification-info button.close:focus,
            .rui-inline-notification-info button.close:active,
            .rui-inline-notification-warning button.close:focus,
            .rui-inline-notification-warning button.close:active,
            .rui-inline-notification-error button.close:focus,
            .rui-inline-notification-error button.close:active {
                color: var(--text-regular, #181830);
                outline: var(--outline-width, 1px) var(--outline-style, solid) var(--btn-primary-border, #3878ab);
                outline-offset: var(--outline-offset-btn, 0);
            }
            
            .rui-inline-notification-success button.close {
                color: var(--notification-success-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-info button.close {
                color: var(--notification-info-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-warning button.close {
                color: var(--notification-warning-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-error button.close {
                color: var(--notification-error-close-btn, rgba(102, 102, 102, 0.5));
            }
            
            .rui-inline-notification-hidden {
                display: none;
            }

            @media print {
                .rui-inline-notification {
                    background-color: transparent;
                    border-color: #000000;
                    color: #000000;
                }
            }
        `;
    }
}
customElements.get(RuiInlineNotification.is) || customElements.define(RuiInlineNotification.is, RuiInlineNotification);
